import { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { Seo } from 'src/components/seo';
import { useAuth } from 'src/hooks/use-auth';
import { useMounted } from 'src/hooks/use-mounted';
import { usePageView } from 'src/hooks/use-page-view';
import { useRouter } from 'src/hooks/use-router';
import { useSearchParams } from 'src/hooks/use-search-params';
import { paths } from 'src/paths';

const initialValues = {
  email: '',
  password: '',
  submit: null,
};

const validationSchema = Yup.object({
  email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
  password: Yup.string().max(255).required('Password is required'),
});

const Page = () => {
  const theme = useTheme();
  const isMounted = useMounted();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        await signIn(values.email, values.password);

        if (isMounted()) {
          router.push(returnTo || paths.setting.index);
        }
      } catch (err) {
        console.error(err);

        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  usePageView();

  const cardRadius = { xs: 2.5, sm: 3 };
  const textFieldSx = {
    '& .MuiFilledInput-root': {
      backgroundColor: alpha(theme.palette.background.paper, 0.5),
      borderRadius: 2,
      overflow: 'hidden',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.65),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
      },
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset !important`,
      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
      caretColor: theme.palette.text.primary,
      transition: 'background-color 9999s ease-out 0s',
    },
  };

  return (
    <>
      <Seo title="Login" />
      <Box
        sx={{
          backgroundColor: 'background.default',
          backgroundImage: 'url("/assets/gradient-bg.svg")',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          width: '100%',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
          >
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                borderRadius: 1.5,
                color: 'primary.main',
                display: 'inline-flex',
                height: 36,
                justifyContent: 'center',
                width: 36,
              }}
            >
              <InventoryOutlinedIcon fontSize="small" />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                fontWeight: 800,
                letterSpacing: '0.3px',
                '& span': { color: 'primary.main' },
              }}
            >
              POS <span>System</span>
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: '1 1 auto',
            justifyContent: 'center',
            px: { xs: 2, sm: 3 },
            pb: { xs: 3, sm: 4 },
            pt: { xs: 0.5, sm: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: cardRadius,
              boxShadow: {
                xs: '0 12px 32px rgba(15, 23, 42, 0.1)',
                sm: '0 20px 50px rgba(15, 23, 42, 0.12)',
              },
              isolation: 'isolate',
              maxWidth: 440,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                px: { xs: 2.5, sm: 4 },
                pt: { xs: 3, sm: 4 },
                pb: { xs: 2.5, sm: 3 },
                textAlign: 'center',
              }}
            >
              <Box
                alt="POS System"
                component="img"
                src="/apple-touch-icon.png"
                sx={{
                  borderRadius: 2,
                  boxShadow: theme.shadows[4],
                  display: 'inline-block',
                  height: { xs: 56, sm: 64 },
                  mb: 2,
                  objectFit: 'cover',
                  width: { xs: 56, sm: 64 },
                }}
              />
              <Typography
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700 }}
                variant="h5"
              >
                Welcome back
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, mt: 0.75 }}
                variant="body2"
              >
                Sign in to your admin dashboard
              </Typography>
            </Box>

            <Box
              sx={{
                px: { xs: 2.5, sm: 4 },
                pt: { xs: 2.5, sm: 3 },
                pb: { xs: 3, sm: 4 },
              }}
            >
              <form
                noValidate
                onSubmit={formik.handleSubmit}
              >
                <Stack spacing={3}>
                  {formik.errors.submit && (
                    <Alert
                      severity="error"
                      sx={{ borderRadius: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {formik.errors.submit}
                    </Alert>
                  )}

                  <TextField
                    autoComplete="email"
                    error={!!(formik.touched.email && formik.errors.email)}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    label="Email Address"
                    name="email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    sx={textFieldSx}
                    type="email"
                    value={formik.values.email}
                  />

                  <TextField
                    autoComplete="current-password"
                    error={!!(formik.touched.password && formik.errors.password)}
                    fullWidth
                    helperText={formik.touched.password && formik.errors.password}
                    label="Password"
                    name="password"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    sx={textFieldSx}
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            edge="end"
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(event) => event.preventDefault()}
                            size="small"
                            sx={{ mr: -0.5 }}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    disabled={formik.isSubmitting}
                    fullWidth
                    size="large"
                    sx={{
                      borderRadius: 2,
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      fontWeight: 600,
                      mt: 0.5,
                      py: { xs: 1.1, sm: 1.35 },
                      textTransform: 'none',
                    }}
                    type="submit"
                    variant="contained"
                  >
                    {formik.isSubmitting ? (
                      <CircularProgress
                        color="inherit"
                        size={22}
                      />
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Page;
